import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { CardPaymentFormFields } from '@cencosud-cencommerce/paris-uikit';
import {
  CardSelect,
  Checkbox,
  TermsLinksAndLabel,
  Text,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { useAppSelector } from '@/Application/Hooks/Redux';
import {
  selectBuyNowCartData,
  selectBuyNowCartIsSummaryOpen
} from '@/Application/Selectors/buyNow/useBuyNowCart';
import {
  BuyNowEventInteractiveTags,
  type SendGtmEvent
} from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import AddressModal from '@/Pages/BuyNowPage/PaymentAndShipping/partials/AddressModal';
import BuyNowCouponComponent from '@/Pages/BuyNowPage/PaymentAndShipping/partials/Coupon/BuyNowCouponComponent';
import { SavedCardPayment } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/SavedCardPayment/SavedCardPayment';
import ShippingMethods from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingMethods/ShippingMethods';
import { useFindLocations } from '@/sections/region/hooks/useFindLocations';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

const LABEL_MODIFY = 'Modificar';
const LABEL_MY_ADDRESS = 'Mi dirección';

const EDIT_ADDRESS_EVENT: SendGtmEvent = {
  event: 'baseEvent',
  extraFields: {
    'event-interactive-tag': BuyNowEventInteractiveTags.BotonEditarDireccion,
    'event-interactive-accion': 'click'
  }
};

export const PaymentAndShipping = () => {
  const { isMobile, isTablet } = useBreakpoint();
  const { findRegionName, findCommuneName } = useFindLocations();
  const cart = useAppSelector(selectBuyNowCartData);
  const isSummaryOpen = useAppSelector(selectBuyNowCartIsSummaryOpen);

  const getFormattedAddress = () => {
    if (!cart?.shippingAddress) {
      return '';
    }

    const { streetName, streetNumber, apartment, region, commune } =
      cart.shippingAddress;

    const regionName = findRegionName(region);
    const communeName = findCommuneName(region, commune);

    const addressParts = [
      streetName,
      streetNumber,
      apartment,
      communeName,
      regionName
    ].filter(Boolean);
    return addressParts.join(', ');
  };

  const [modalOpen, setModalOpen] = useState<
    null | 'address' | 'cardPayment' | 'pickCard'
  >(null);

  const { control } = useFormContext<CardPaymentFormFields>();

  const onEditAddress = () => {
    sendEvent(EDIT_ADDRESS_EVENT);
    setModalOpen('address');
  };

  return (
    <section
      className={classNames(
        'co-flex co-flex-col co-gap-5 co-px-4 tablet_w:co-px-0',
        !isSummaryOpen && 'co-mb-[190px]'
      )}
    >
      <CardSelect
        className="co-p-4 co-pr-6"
        ctaComponent={
          <button onClick={onEditAddress} className="ui-focus-2">
            <Text color="primary-500" variant="link2" weight={600}>
              {LABEL_MODIFY}
            </Text>
          </button>
        }
        description1={getFormattedAddress()}
        iconName="place"
        title={LABEL_MY_ADDRESS}
      />

      <SavedCardPayment />

      <ShippingMethods />

      {(isMobile || isTablet) && <BuyNowCouponComponent />}

      <AddressModal
        data-testid="product-services-modal"
        isOpen={modalOpen === 'address'}
        onClose={() => setModalOpen(null)}
      />

      {(isMobile || isTablet) && (
        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <Checkbox
              data-testid="card-payment-terms-checkbox"
              id="terms"
              label={<TermsLinksAndLabel />}
              checked={!!field.value}
              disabled={field.disabled}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              name={field?.name}
            />
          )}
        />
      )}
      <LocalHookFormDevtool control={control} />
    </section>
  );
};

PaymentAndShipping.displayName = 'PaymentAndShipping';

export default PaymentAndShipping;
